import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { IonCard, IonImg, IonItem, IonPopover, IonText, isPlatform } from '@ionic/react'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { PlusOutlined } from '@ant-design/icons'

import { useTranslation } from 'react-i18next'
import imageCompression from 'browser-image-compression'

import { FilesystemRoutes } from '@src/global'
import { FileContext, OfflineHandlerContext } from '@src/components/Mobile/Providers'
import { ToastContext } from '@src/components/Providers'

import { StyledIonItemGroup, StyledUpload } from './FileUpload.styles'

interface FileUploadProps {
  fileName: string
  folderName: FilesystemRoutes
}

export const FileUpload: FC<FileUploadProps> = ({ folderName, fileName }) => {
  const { t } = useTranslation()
  const { saveFile, getFileAsBase64 } = useContext(FileContext)
  const { setIonToast } = useContext(ToastContext)
  const { addJob } = useContext(OfflineHandlerContext)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [showPopover, setShowPopover] = useState(false)
  const galleryInputRef = useRef<any>(null)

  const showErrorToast = useCallback(() => {
    setIonToast({
      show: true,
      message: t('containers.fileUpload.fileSystemError'),
    })
  }, [setIonToast, t])

  const processAndUpload = async ({
    file,
    fileNameWithExtension,
    fileType,
  }: {
    file: File
    fileNameWithExtension: string
    fileType?: string
  }) => {
    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType,
      })

      const savedFile = await saveFile(compressedFile, folderName, fileNameWithExtension)

      addJob({
        fileName: fileNameWithExtension,
        file: compressedFile,
      })

      setImageUrl(savedFile)
    } catch (error) {
      showErrorToast()
    }
  }

  /**
   * Side Effect:
   * Look if file for fileName is already existing and show it to the user
   */
  useEffect(() => {
    async function checkFile() {
      try {
        const imageBase64 = await getFileAsBase64(folderName, fileName)

        setImageUrl(imageBase64)
      } catch (error) {
        // This just means file does not exist yet - which is okay
      }
    }

    checkFile()
  }, [getFileAsBase64, setImageUrl, fileName, folderName])

  /**
   * Change Handler
   * Handle change of file: save to filesystem and add job to offlineHandler queue
   */
  const handleFileChange = async ({ file }) => {
    setShowPopover(false)
    const mimeType = file.name.split('.').pop()
    const fileNameWithExtension = `${fileName}.${mimeType}`

    await processAndUpload({
      file,
      fileNameWithExtension,
    })
  }

  const takePicture = async () => {
    setShowPopover(false)

    try {
      const image = await Camera.getPhoto({
        quality: 50,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      })

      const fileNameWithExtension = `${fileName}.jpg`
      if (!image?.dataUrl) return showErrorToast()

      const fileFromCamera = await imageCompression.getFilefromDataUrl(image.dataUrl, fileNameWithExtension)

      await processAndUpload({
        file: fileFromCamera,
        fileNameWithExtension,
        fileType: 'image/jpeg',
      })
    } catch {
      showErrorToast()
    }
  }

  const handleCameraFileChange = (event: any) => {
    const file = event.target?.files[0]

    if (file) {
      handleFileChange({ file })
    }

    // TODO: in the future we need to enable the app to sleep again in ios, right now it is not possible to do something
    //   after a picture was taken or the process is cancelled. In the future we will change this component most likely.
    //   For now we will keep it as it is.
  }

  const openGallery = () => {
    galleryInputRef?.current?.click()
  }

  const handleUploadClick = () => {
    if (isPlatform('ios')) {
      // ios handles switching camera and gallery natively
      // so we open the gallery directly
      openGallery()
    } else {
      // for android we show options between camera and gallery
      setShowPopover(true)
    }
  }

  return (
    <>
      <input type='file' accept='image/*' onChange={handleCameraFileChange} ref={galleryInputRef} hidden />

      <IonPopover isOpen={showPopover} onDidDismiss={() => setShowPopover(false)}>
        <StyledIonItemGroup>
          <IonItem onClick={openGallery}>
            <IonText>{t('containers.fileUpload.chose')}</IonText>
          </IonItem>

          <IonItem onClick={takePicture}>
            <IonText>{t('containers.fileUpload.capture')}</IonText>
          </IonItem>
        </StyledIonItemGroup>
      </IonPopover>

      <IonCard>
        <StyledUpload onClick={handleUploadClick}>
          {imageUrl ? (
            <IonImg src={imageUrl} />
          ) : (
            <>
              <PlusOutlined />

              <div className='ant-upload-text'>{t('mobile.pages.orders.detail.uploadCustomer')}</div>
            </>
          )}
        </StyledUpload>
      </IonCard>
    </>
  )
}
